import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Introduction to React & the Virtual DOM
//  Chapter 1 · Intro
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "gold";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_2 = `// React under the hood — JSX compiles to createElement calls
const element = <h1 className="title">Hello React</h1>;

// Babel transforms this to:
const element = React.createElement(
  'h1',
  { className: 'title' },
  'Hello React'
);

// React stores this as a plain JS object (Virtual DOM node):
{
  type: 'h1',
  props: { className: 'title', children: 'Hello React' },
}`;

const CODE_3 = `import { createRoot } from 'react-dom/client';
import App from './App';

// React 18 — createRoot (replaces ReactDOM.render)
const root = createRoot(document.getElementById('root'));
root.render(<App />);`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function ReactIntroAndVirtualDOM() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 1 · Intro</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Introduction to React & the Virtual DOM</h1>
        <p style={styles.desc}>Understand what React is, why it exists, and how the Virtual DOM makes UIs fast.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── What is React? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>What is React?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "React is an open-source JavaScript library created by Meta (Facebook) in 2013 for building fast, component-based user interfaces. Unlike full frameworks, React focuses solely on the View layer, letting you compose complex UIs from small, isolated pieces called <b>components</b>. React can run in the browser (web), on mobile (React Native), and even on the server (React Server Components)." }} />
      </div>

      /* ── Why React? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Why React?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>Component reusability</b> — write once, use everywhere" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>Declarative</b> — describe what the UI should look like, React handles updates" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>Unidirectional data flow</b> — predictable, easy to debug" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>Huge ecosystem</b> — routing, state, testing, animation and more" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>React Native</b> — share logic between web and mobile" }} />
      </div>

      /* ── The Virtual DOM ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>The Virtual DOM</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "The real DOM is slow to update because every change triggers reflow and repaint. React solves this with a <b>Virtual DOM</b> — a lightweight in-memory copy of the real DOM tree. When state changes:" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "1. React builds a new Virtual DOM tree" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "2. <b>Diffing</b>: compares new tree vs previous tree" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "3. <b>Reconciliation</b>: computes the minimum set of changes" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "4. Applies only those changes to the real DOM (batch update)" }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── React 18 Key Features ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>React 18 Key Features</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>Concurrent Mode</b> — render work can be paused, resumed, or abandoned" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>Automatic Batching</b> — multiple state updates grouped into one re-render" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>useTransition &amp; useDeferredValue</b> — mark non-urgent updates" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>Suspense on the server</b> — stream HTML progressively" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>createRoot API</b> — new entry point replacing ReactDOM.render" }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_3, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_3)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What problem does the Virtual DOM solve?</div>
          <div style={styles.qItem}>✦ 2. What is reconciliation in React?</div>
          <div style={styles.qItem}>✦ 3. How does React differ from Angular or Vue?</div>
          <div style={styles.qItem}>✦ 4. What is the difference between a library and a framework?</div>
          <div style={styles.qItem}>✦ 5. Name three new features introduced in React 18.</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
